import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './PortalHelp.css';

const SEARCH_DEBOUNCE_MS = 250;

// One component for both /portal/help (searchable list) and
// /portal/help/:id (a single article), so the two share the page chrome.
function PortalHelp() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [articles, setArticles] = useState([]);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (id) return undefined;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        setArticles(await apiClient.get(`/api/kb/published?q=${encodeURIComponent(search.trim())}`));
        setError('');
      } catch (err) {
        console.error('Error loading help articles:', err);
        setError('We couldn’t load the help articles. Please try again.');
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [id, search]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setArticle(null);
    apiClient.get(`/api/kb/published/${id}`)
      .then((data) => { setArticle(data); setError(''); })
      .catch(() => setError('That article couldn’t be found. It may have been removed.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (id) {
    return (
      <PortalShell>
        <div className="portal-help">
          <Link to="/portal/help" className="portal-help-back">&larr; All help articles</Link>
          {loading ? (
            <p aria-live="polite">Loading&hellip;</p>
          ) : error ? (
            <div className="portal-help-error">{error}</div>
          ) : (
            <article className="portal-help-article">
              {article.category && <div className="portal-help-category">{article.category}</div>}
              <h1>{article.title}</h1>
              <div className="portal-help-updated">Updated {new Date(article.updatedAt).toLocaleDateString('en-GB')}</div>
              <div className="portal-help-body">{article.body}</div>
              <div className="portal-help-cta">
                Still stuck? <Link to="/portal/tickets">Open a support ticket</Link> and we&rsquo;ll help.
              </div>
            </article>
          )}
        </div>
      </PortalShell>
    );
  }

  const byCategory = articles.reduce((groups, a) => {
    const key = a.category || 'General';
    (groups[key] = groups[key] || []).push(a);
    return groups;
  }, {});

  return (
    <PortalShell>
      <div className="portal-help">
        <h1>Help Centre</h1>
        <p className="portal-help-intro">Answers to common questions. Can&rsquo;t find what you need? <Link to="/portal/tickets">Open a support ticket</Link>.</p>

        <input
          type="search"
          className="portal-help-search"
          aria-label="Search help articles"
          placeholder="Search for an answer…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {error ? (
          <div className="portal-help-error">{error}</div>
        ) : loading ? (
          <p aria-live="polite">Loading&hellip;</p>
        ) : articles.length === 0 ? (
          <p className="portal-help-empty">{search.trim() ? 'No articles match your search.' : 'There are no help articles yet.'}</p>
        ) : (
          Object.entries(byCategory).map(([category, items]) => (
            <section key={category} className="portal-help-group">
              <h2>{category}</h2>
              <ul>
                {items.map((a) => (
                  <li key={a.id}><Link to={`/portal/help/${a.id}`}>{a.title}</Link></li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </PortalShell>
  );
}

export default PortalHelp;
